'use client'

import React, { forwardRef, useEffect, useState } from 'react'

import { ColorScheme, Tag, TagProps } from '../Tag/Tag'
import { useTagLayout } from './TagGroup.utils'
import { TagGroupNoWrap } from './TagGroupNoWrap'
import { TagGroupWrap } from './TagGroupWrap'

export type TagGroupProps = {
  children: React.ReactNode
  className?: string
  /** When true, tags wrap onto new lines when they exceed the container width */
  wrap?: boolean
  /**
   * Maximum width per tag before the label is truncated with ellipsis.
   * Applies as a per-tag cap when wrap=false, and as the container width cap when wrap=true.
   * Defaults to 160px when wrap=false.
   */
  maxTagWidth?: string | number
  /** When true, reserves space for the × button so hovering a tag doesn't cause overflow */
  editable?: boolean
  /**
   * Called when the "+" add button is clicked. Only rendered when editable=true and wrap=true
   * and no addInput is provided.
   */
  onAdd?: () => void
  /**
   * Render prop for an inline search input shown when the "+" button is clicked.
   * Receives an `onClose` callback to hide the input and restore the "+" button.
   * Only rendered in wrap=true and editable=true mode.
   */
  addInput?: (onClose: () => void) => React.ReactNode
  /** Color scheme for all child tags. Defaults to 'dark'. */
  colorScheme?: ColorScheme
}

export const TagGroup = forwardRef<HTMLDivElement, TagGroupProps>(
  function TagGroup(
    {
      children,
      className,
      wrap = false,
      maxTagWidth = 160,
      editable = false,
      onAdd,
      addInput,
      colorScheme = 'dark',
    },
    ref
  ) {
    const [isSearchShowing, setIsSearchShowing] = useState(false)
    const canShowAddInput = wrap && editable && !!addInput
    const hasAddInput = canShowAddInput && isSearchShowing

    useEffect(() => {
      if (!canShowAddInput) setIsSearchShowing(false)
    }, [canShowAddInput])

    const childArray = React.Children.toArray(children).filter(
      (child): child is React.ReactElement<TagProps> =>
        React.isValidElement(child) && child.type === Tag
    )

    const { visibleCount, rowBreaks, refCallback } = useTagLayout(
      wrap,
      editable,
      childArray.length,
      ref,
      hasAddInput
    )
    const effectiveMaxWidth = wrap ? '100%' : maxTagWidth

    const handleAddClick = addInput ? () => setIsSearchShowing(true) : onAdd
    const addInputNode = hasAddInput
      ? addInput!(() => setIsSearchShowing(false))
      : undefined

    // Wrap mode: render tags in measured rows.
    // rowBreaks is empty on first render (or during re-layout after children change).
    // When the search input is showing, fall back to [0] (single row) instead of switching to
    // TagGroupNoWrap, so TagSearchInput stays mounted and preserves its query state.
    const effectiveRowBreaks =
      rowBreaks.length > 0 ? rowBreaks : hasAddInput ? [0] : []

    if (wrap && effectiveRowBreaks.length > 0) {
      return (
        <TagGroupWrap
          addInput={addInputNode}
          childArray={childArray}
          className={className}
          colorScheme={colorScheme}
          editable={editable}
          effectiveMaxWidth={effectiveMaxWidth}
          onAdd={handleAddClick}
          refCallback={refCallback}
          rowBreaks={effectiveRowBreaks}
        />
      )
    }

    return (
      <TagGroupNoWrap
        childArray={childArray}
        className={className}
        colorScheme={colorScheme}
        effectiveMaxWidth={effectiveMaxWidth}
        refCallback={refCallback}
        visibleCount={visibleCount}
      />
    )
  }
)
